import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "./Seo";
import Reveal from "./Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import GuideReviews from "./GuideReviews";
import { LegalDisclaimer } from "./LegalDisclaimer";

export default function GuideLayout({
  testid,
  seo,
  overline,
  h1,
  intro,
  heroImage,
  heroAlt,
  blocks = [],
  cards,
  note,
  faq,
  cta,
}) {
  return (
    <div className="pt-28" data-testid={testid}>
      <Seo {...seo} />

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto">
        <Reveal>
          <p className="overline mb-4">{overline}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none max-w-4xl">
            {h1}
          </h1>
          <p className="text-white/60 mt-8 max-w-2xl leading-relaxed">{intro}</p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto mt-16">
        <Reveal>
          <div className="relative h-[320px] md:h-[520px] overflow-hidden">
            <img src={heroImage} alt={heroAlt} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </div>
        </Reveal>
      </section>

      {blocks.length > 0 && (
        <section className="px-6 md:px-12 max-w-[1400px] mx-auto mt-24 grid grid-cols-1 md:grid-cols-2 gap-12">
          {blocks.map((b) => (
            <Reveal key={b.title}>
              <div className="border-t border-white/10 pt-8">
                <div className="w-8 h-px bg-[#C5A059] mb-5" />
                <h2 className="font-serif text-2xl md:text-3xl font-light mb-4">{b.title}</h2>
                <p className="text-white/60 leading-relaxed text-sm md:text-base">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </section>
      )}

      {cards && (
        <section className="px-6 md:px-12 max-w-[1400px] mx-auto mt-28">
          <Reveal>
            <p className="overline mb-4">{cards.overline}</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light mb-12 max-w-3xl">{cards.title}</h2>
          </Reveal>
          <div className={`grid grid-cols-1 md:grid-cols-2 ${cards.items.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-8`}>
            {cards.items.map((c) => (
              <Reveal key={c.title}>
                <div className="border border-white/10 p-7 h-full hover:border-[#C5A059]/40 transition-colors">
                  <div className="w-6 h-px bg-[#C5A059] mb-4" />
                  <h3 className="font-serif text-xl font-light mb-3">{c.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {note && (
        <section className="px-6 md:px-12 max-w-[1400px] mx-auto mt-28">
          <Reveal>
            <div className="max-w-3xl">
              <div className="w-8 h-px bg-[#C5A059] mb-5" />
              <h2 className="font-serif text-2xl md:text-3xl font-light mb-4">{note.title}</h2>
              <p className="text-white/60 leading-relaxed text-sm md:text-base">{note.body}</p>
            </div>
          </Reveal>
        </section>
      )}

      {faq && faq.items && faq.items.length > 0 && (
        <section className="px-6 md:px-12 max-w-[900px] mx-auto mt-28" data-testid={`${testid}-faq`}>
          <Reveal>
            <p className="overline mb-4">{faq.overline}</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light mb-8">{faq.title}</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faq.items.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-white text-base md:text-lg font-serif font-light py-5 hover:no-underline hover:text-[#C5A059]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 text-sm md:text-base leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>
      )}

      <GuideReviews />

      <section className="px-6 md:px-12 max-w-[1400px] mx-auto mt-28 mb-32">
        <Reveal>
          <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <h2 className="font-serif text-3xl md:text-4xl font-light max-w-xl">{cta.title}</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={cta.secondaryHref}
                data-testid={`${testid}-secondary-cta`}
                className="inline-flex items-center gap-2 px-8 py-4 border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors"
              >
                {cta.secondaryLabel} <ArrowRight size={18} />
              </Link>
              <Link
                to={cta.primaryHref}
                data-testid={`${testid}-primary-cta`}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#C5A059] text-black hover:bg-[#d4b073] transition-colors"
              >
                {cta.primaryLabel} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 max-w-[1400px] mx-auto pb-24">
        <LegalDisclaimer className="border-t border-white/10 pt-8 max-w-4xl" />
      </section>
    </div>
  );
}
